import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Stack,
    Box,
    Typography,
    Button,
    Divider,
    Skeleton,
    Pagination,
    FormControl,
    InputLabel,
    Select,
    Tooltip,
    IconButton,
    MenuItem,
    Tab,
    Tabs,
} from '@mui/material';
import RoutePaths from '../routes/RoutePaths';
import Markdown from 'react-markdown';
import examples from '../data/article-examples';
import Article, { FullScreenArticle, MainUserNeed } from './../components/Article';
import { useGetSearchResults, useGetSearches } from '../api/ContentApi';
import { usePostSearch } from '../api/ContentApi';
import ResultChat from './chat/ResultChat';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    ArrowUpward,
    ArrowDownward,
    ExpandLess,
    ExpandMore
} from '@mui/icons-material';
import InfoIcon, { InfoTooltip } from './InfoIcon';

const PAGING_LIMIT = 6;
const DEBUG = false;
const FEATURE_SEARCH_RESULT_CHAT = process.env.REACT_APP_FEATURE_SEARCH_RESULT_CHAT === "true"
const FEATURE_ANALYSIS_METADATA = process.env.REACT_APP_FEATURE_ANALYSIS_METADATA === "true"

const SORT_ORDER = {
    asc: "asc",
    desc: "desc"
}

const SORT_OPTIONS = [
    "relevance_score",
    "publish_date",
    "metadata.page_views"
]


function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 0 }} mt={props.mt ? props.mt : 0}>{children}</Box>}
        </div>
    );
}

const ResultArticles = ({
    search,
    articles,
    hideRelevance,
}) => {
    const navigate = useNavigate();
    const [openArticle, setOpenArticle] = useState(false);

    const searchMutation = usePostSearch({
        onSuccess: (data) => {
            if (DEBUG) console.log("ResultArticles :: searchMutation :: onSuccess ", data);
            navigate(`${RoutePaths.root.search}/${data.result.id}`);
        },
        onError: (error) => {
            console.error("ResultArticles :: searchMutation :: onError ", error);
        }
    });

    const send = (message, context) => {
        if (DEBUG) console.log("ResultArticles :: send");
        if (DEBUG) console.log(search);
        if (DEBUG) console.log(message);
        //setMessages([...messages, { created: Date.now(), message_content: message, role: 'user' }]);

        searchMutation.mutate({
            query: message,
            filters: search.filters,
            content_ids: context,
        });
    };

    return <>
        <FullScreenArticle
            article={openArticle}
            open={!!openArticle}
            sendAction={send}
            sendMutation={searchMutation}
            setOpen={() => setOpenArticle(null)} />
        {
            articles.map((article, index) => {
                return (
                    <Article
                        key={index}
                        brand={article.brand}
                        date={article.publish_date}
                        title={article.title}
                        score={hideRelevance ?
                            null : article.relevance_score}
                        summary={article.summary}
                        actions={true}
                        sub={
                            <span><FormattedMessage id="article.userneed.drive" />: <MainUserNeed metadata={article.metadata} /></span>
                        }
                        openAction={() => setOpenArticle(article)}
                    />
                );
            })
        }
    </>

}

const SortOptions = ({
    configuration,
    sortOption,
    setSortOption,
    sortOrder,
    setSortOrder
}) => {
    const intl = useIntl();
    const ascendingLabel = intl.formatMessage({ id: "search.sort.order.asc" });
    const descendingLabel = intl.formatMessage({ id: "search.sort.order.desc" });

    if (configuration) {
        return <Stack
            width="100%"
            direction="row"
            spacing={2}
            justifyContent="start"
            alignItems="center">
            <Typography variant="body1" style={{ textAlign: "left" }}>
                <FormattedMessage id="search.sort.select" />
            </Typography>
            <FormControl size="small">
                <Select
                    labelId="dropdown-label"
                    value={sortOption}
                    onChange={(e => setSortOption(e.target.value))}
                >
                    {configuration.sortOptions.map((option, index) => (
                        <MenuItem key={index} value={option}>
                            <FormattedMessage id={`search.sort.options.${option}`} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Tooltip
                title={
                    `${sortOrder === 'asc' ?
                        ascendingLabel : descendingLabel}`}
            >
                <IconButton
                    size="small"
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                >
                    {
                        sortOrder === 'asc' ?
                            <span>▲</span>
                            : <span>▼</span>
                    }
                </IconButton>
            </Tooltip>
            <InfoIcon id="info.query.results.relevance" />
        </Stack>
    } else {
        if (DEBUG) console.error("SortOptions :: configuration is not ready");
        return null;
    }
}

const SearchResultPagination = ({
    searchResults,
    page,
    setPage
}) => {
    const [stickyTotal, setStickyTotal] = useState(null);

    useEffect(() => {
        if (searchResults.data) {
            setStickyTotal(
                searchResults.data.result.result_count);
        }
    }, [searchResults.data]);

    if (searchResults.data) {
        const data = searchResults.data.result;
        if (DEBUG) console.log("SearchResultPagination :: data.result_count", data.result_count);

        if (data.result_count <= PAGING_LIMIT) {
            return null;
        } else {
            return <Pagination
                count={
                    Math.ceil(data.result_count / PAGING_LIMIT)
                }
                color="primary"
                page={page}
                onChange={(event, value) => setPage(value)}
                size="large"
                disabled={!searchResults.data && searchResults.isFetching}
            />
        }
    } else if (stickyTotal > PAGING_LIMIT) {
        return <Pagination
            count={
                Math.ceil(stickyTotal / PAGING_LIMIT)
            }
            color="primary"
            page={page}
            size="large"
            disabled={true}
        />
    }
}

const SearchResult = ({ search, configuration, searchQuery }) => {
    const [page, setPage] = useState(1);
    const [stickyItemCount, setStickyItemCount] = useState(null);
    const [articleCount, setArticleCount] = useState(null);
    const [chatMessageCount, setChatMessageCount] = useState(0);
    const [selectedTab, setSelectedTab] = useState(0);
    const [sortOption, setSortOption] = useState(configuration ?
        configuration.sortOptions[0] :
        SORT_OPTIONS[0]);
    const [sortOrder, setSortOrder] = useState(SORT_ORDER.desc);
    const noQuery = search.query === "";
    const paginationRef = useRef(null);


    const searchResults = useGetSearchResults({
        parameters: {
            id: search.id,
            limit: PAGING_LIMIT,
            page: page - 1,
            sort_rule: sortOption,
            sort_order: sortOrder
        }
    });

    useEffect(() => {
        if (searchResults.data) {
            setStickyItemCount(
                searchResults.data.result.result_count > PAGING_LIMIT ?
                    PAGING_LIMIT :
                    searchResults.data.result.result_count);
            setArticleCount(
                searchResults.data.result.result_count);
        }
    }, [searchResults.data]);

    useEffect(() => {
        if (chatMessageCount > 1 ||
            // ..but count "" query and a message as a conversation
            chatMessageCount > 0 && searchQuery?.data?.result?.query == "") {
            setSelectedTab(1);
        } else {
            setSelectedTab(0);
        }
    }, [chatMessageCount]);

    useEffect(() => {
        setPage(1);
        //setStickyItemCount(null);
        //setArticleCount(null);
    }, [search.id]);
    useEffect(() => {
        setPage(1);
    }, [sortOption, sortOrder]);

    useEffect(() => {
        if (stickyItemCount != null) {
            paginationRef.current?.scrollIntoView(
                { behavior: "smooth", block: "end" });
        }
    }, [page]);

    useEffect(() => {
        if (searchQuery?.data?.result?.chat_session?.chat_messages) {
            setChatMessageCount(searchQuery.data.result.chat_session.chat_messages.length);
        }
    }, [searchQuery.data]);

    const isLoading = stickyItemCount == null || !searchQuery.data;

    if (searchResults.isError) {
        console.error("SearchResult :: searchResults isError", searchResults.error);
        return (
            <>
                <Divider />
                <Typography variant="body1">
                    <FormattedMessage id="error.results" />
                </Typography>
            </>
        );
    }

    return (
        <>
            <Divider />
            <Stack spacing={1}>
                <Typography variant="h4" style={{ textAlign: "left" }}>
                    <FormattedMessage id="search.results.title" />
                </Typography>
                {
                    isLoading &&
                    <>
                        <Skeleton
                            variant="rectangular"
                            width="100%"
                            height={48} />
                        <br />
                        {
                            Array.from({
                                length: Math.max(3, stickyItemCount)
                            }, (_, i) => i).map(
                                (index) => <Skeleton
                                    key={index}
                                    variant="rectangular"
                                    width="100%"
                                    height={170} />
                            )
                        }
                    </>
                }
                {
                    !isLoading &&
                    <Box>
                        <Tabs
                            value={selectedTab}
                            onChange={(event, value) => { setSelectedTab(value) }}>
                            <Tab label={
                                <InfoTooltip
                                    title={<FormattedMessage id="info.query.results" />} >
                                    <FormattedMessage
                                        id="search.results.articles" /> {
                                        articleCount > 0 &&
                                        <span>({articleCount} <FormattedMessage id="search.results.count" />)</span>
                                    }
                                </InfoTooltip>
                            } />
                            <Tab label={
                                <span>
                                    <FormattedMessage
                                        id="search.results.conversation" /> {
                                        chatMessageCount > 1 &&
                                        <span>({chatMessageCount - 1} <FormattedMessage id="search.results.messages" />)</span>
                                    }
                                </span>
                            } />
                        </Tabs>
                        <CustomTabPanel value={selectedTab} index={0} mt={3}>
                            {
                                searchResults.data &&
                                searchResults.data.result &&
                                searchResults.data.result.result_count === 0 &&
                                <Box>
                                    <Typography variant="body">
                                        <FormattedMessage id="search.results.empty" />
                                    </Typography>
                                </Box>
                            }
                            {

                                <Stack spacing={1}>
                                    {
                                        searchResults.data &&
                                        searchResults.data.result &&
                                        searchResults.data.result.result_count > 0 &&
                                        <SortOptions
                                            {...{
                                                configuration,
                                                sortOption,
                                                sortOrder,
                                                setSortOption: (option) => {
                                                    if (DEBUG) console.log("SearchResult :: setSortOption", option);
                                                    setSortOption(option);
                                                },
                                                setSortOrder: (order) => {
                                                    if (DEBUG) console.log("SearchResult :: setSortOrder", order);
                                                    setSortOrder(order);
                                                }
                                            }} />
                                    }
                                    <Box
                                        overflow="auto">
                                        <Stack direction="column" spacing={2}>
                                            {
                                                !searchResults.data &&
                                                searchResults.isFetching &&
                                                Array.from({
                                                    length: Math.max(3, stickyItemCount)
                                                }, (_, i) => i).map(
                                                    (index) => <Skeleton
                                                        key={index}
                                                        variant="rectangular"
                                                        width="100%"
                                                        height={170} />
                                                )
                                            }
                                            {
                                                searchResults.data &&
                                                !searchResults.isLoading &&
                                                !searchResults.isError &&
                                                <ResultArticles
                                                    {...{
                                                        hideRelevance: noQuery,
                                                        articles: searchResults.data.result.results,
                                                        search,
                                                    }}
                                                />
                                            }
                                        </Stack>
                                    </Box>
                                </Stack>
                            }
                            <Box ref={paginationRef} sx={{ pb: 2, mt: 2 }}>
                                <SearchResultPagination
                                    {...{
                                        searchResults,
                                        page,
                                        setPage
                                    }} />
                            </Box>
                        </CustomTabPanel>
                        {
                            FEATURE_SEARCH_RESULT_CHAT &&
                            <CustomTabPanel value={selectedTab} index={1} mt={0}>
                                <ResultChat
                                    search={search}
                                    searchQuery={searchQuery}
                                />
                            </CustomTabPanel>
                        }
                    </Box>
                }
            </Stack>
        </>
    )
}

export default SearchResult;